// src/pages/NotasFiscais.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  getFirestore, collection, doc, getDocs,
  query, where, runTransaction, serverTimestamp, updateDoc, deleteField, orderBy,
  startAt, endAt, limit
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import axios from 'axios';
import { auth } from '../firebaseConfig';
import { useEmpresaId } from '../context/EmpresaContext';

import { useNavigate } from 'react-router-dom';
import { Search, BookOpen } from 'lucide-react';
import {
  FaFileInvoiceDollar, FaPlus, FaTrashAlt, FaSyncAlt,
  FaFileDownload, FaExternalLinkAlt, FaEye
} from 'react-icons/fa';

/* ====== Constantes/Helpers ====== */
const db = getFirestore();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
const DEBUG_NF = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_NF === '1';
const nfLog = (...args) => { if (DEBUG_NF) console.info('[NF AVULSA]', ...args); };

const ensureFocusAbsolute = (maybePath) =>
  maybePath && !/^https?:\/\//i.test(maybePath)
    ? `https://api.focusnfe.com.br${maybePath}`
    : maybePath || '';

const nfseViewUrl = (ref) =>
  `${BACKEND_URL}/api/focus/visualizar-nfse/${encodeURIComponent(ref)}`;

async function authHeader() {
  const idToken = await auth.currentUser?.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

/* ====== Chips de status ====== */
const nfStatusView = (status = '') => {
  const s = String(status || '').toLowerCase();
  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('autoriz')) return { text: 'Autorizada', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('deneg')) return { text: 'Denegada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha')) return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('process')) return { text: 'Processando', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  if (s.includes('aguard') || s.includes('pend')) return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};
const nfseStatusView = (status = '') => {
  const s = String(status || '').toLowerCase();
  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('autoriz') || s.includes('emitida') || s.includes('aprov')) return { text: 'Emitida', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha')) return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('process')) return { text: 'Processando', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  if (s.includes('aguard') || s.includes('pend')) return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};
const Chip = ({ text, className = '', dotClass = '' }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-semibold px-2 py-1 rounded border ${className}`}>
    <i className={`w-2 h-2 rounded-full ${dotClass}`} />
    {text}
  </span>
);

/* ====== UI helpers ====== */
const toneStyles = (tone) => {
  switch (tone) {
    case 'primary': return 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900';
    case 'success': return 'text-green-800 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/50 dark:hover:bg-green-900';
    case 'warning': return 'text-yellow-800 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/50';
    case 'danger':  return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900';
    default:        return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
  }
};

/* Tooltip embutido */
const ActionIcon = ({ as='button', tone='neutral', title, tooltip, disabled, className='', children, ...props }) => {
  const base = `inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-semibold transition ${toneStyles(tone)} ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`;
  const label = tooltip || title;

  const Control = as === 'a'
    ? (p) => <a className={base} title={title} aria-label={label} {...p}>{children}</a>
    : (p) => <button className={base} title={title} aria-label={label} disabled={disabled} {...p}>{children}</button>;

  if (!label) return <Control {...props} />;

  return (
    <div className="relative inline-block group">
      <Control {...props} />
      <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 z-20 rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-lg whitespace-nowrap transition-opacity group-hover:opacity-100">
        {label}
        <span className="absolute left-1/2 top-full -translate-x-1/2 w-0 h-0 border-x-6 border-x-transparent border-t-6 border-t-gray-800" />
      </div>
    </div>
  );
};

/* ====== Paths/coleções ====== */
const colNotas = (empresaId) => collection(db, 'empresas', empresaId, 'notas');
const refContadorNotas = (empresaId) => doc(db, 'empresas', empresaId, 'contadores', 'notas');

/* ====== Emissão / Consulta ====== */
// Chamando direto as rotas existentes (sem 404 de “avulsa”)
async function emitirNFeAvulsa(empresaId, payload) {
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/emissao-nfe/${empresaId}`,
    { ...payload, osId: null },
    { headers: await authHeader() }
  );
  nfLog('emitirNFe OK', data);
  return data;
}
async function emitirNFSeAvulsa(empresaId, payload) {
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/emissao-nfse/${empresaId}`,
    { ...payload, osId: null },
    { headers: await authHeader() }
  );
  nfLog('emitirNFSe OK', data);
  return data;
}
async function consultarNFePorRef(nfeRef) {
  const { data } = await axios.get(
    `${BACKEND_URL}/api/focus/consultar-nfe/${encodeURIComponent(nfeRef)}`,
    { headers: await authHeader() }
  );
  return data;
}
async function consultarNFSePorRef(ref) {
  const { data } = await axios.get(
    `${BACKEND_URL}/api/focus/consultar-nfse/${encodeURIComponent(ref)}`,
    { headers: await authHeader() }
  );
  return data;
}

/* ====== Cancelamento (rotas AVULSAS) ====== */
async function cancelarNFeAvulsaApi(empresaId, { ref, notaId, justificativa }) {
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/cancelar-nfe-avulsa/${empresaId}`,
    { ref, notaId, justificativa },
    { headers: await authHeader() }
  );
  return data;
}
async function cancelarNFSeAvulsaApi(empresaId, { ref, notaId, justificativa }) {
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/cancelar-nfse-avulsa/${empresaId}`,
    { ref, notaId, justificativa },
    { headers: await authHeader() }
  );
  return data;
}

/* ====== Página ====== */
const defaultForm = {
  tipo: 'nfe',           // 'nfe' | 'nfse'
  clienteId: '',
  produtos: [],          // [{...}]
  servicos: [],          // [{...}]
  valorTotalProdutos: 0,
  valorTotalServicos: 0,
  valorTotalGeral: 0
};

// Helper robusto para pegar o valor do serviço independente do nome do campo
const getServicoValor = (s) => {
  const v = s?.valor ?? s?.valorSugerido ?? s?.preco ?? s?.precoSugerido ?? 0;
  return Number(v) || 0;
};

export default function NotasFiscais() {
  const empresaId = useEmpresaId();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [produtosPath, setProdutosPath] = useState('auto'); // 'nested' | 'top' | 'auto'

  const [form, setForm] = useState(defaultForm);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [notas, setNotas] = useState([]);
  const [buscaNotas, setBuscaNotas] = useState('');
  const [modalNota, setModalNota] = useState(null); // doc de nota avulsa aberta no modal

  const [loadingEmit, setLoadingEmit] = useState(false);
  const [loadingRefresh, setLoadingRefresh] = useState({});
  const [loadingCancel, setLoadingCancel] = useState({});

  // Busca de produtos (como no Orcamentos.jsx)
  const [buscaProduto, setBuscaProduto] = useState('');
  const [resultadosBuscaProdutos, setResultadosBuscaProdutos] = useState([]);
  const [buscandoProdutos, setBuscandoProdutos] = useState(false);


  // Busca de serviços (layout igual ao de produtos)
  const [buscaServico, setBuscaServico] = useState('');

  const normalize = useCallback((s) =>
    (s ?? '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''), []);

  useEffect(() => {
    if (!empresaId) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        await fetchDados(empresaId);
        await fetchNotas(empresaId);
      } catch (e) {
        console.error(e);
        Swal.fire('Erro', 'Não foi possível carregar dados da tela de Notas.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [empresaId]);

  useEffect(() => {
    const vp = (form.produtos || []).reduce((acc, p) => acc + (Number(p.precoVenda)||0) * (Number(p.quantidade)||0), 0);
    const vs = (form.servicos || []).reduce((acc, s) => acc + (Number(s.valor)||0) * (Number(s.quantidade||1)), 0);
    setForm(f => ({ ...f, valorTotalProdutos: vp, valorTotalServicos: vs, valorTotalGeral: vp + vs }));
  }, [form.produtos, form.servicos]);

  async function fetchPreferNestedWithPath(empresaIdParam, nomeColecao) {
    const nestedRef = collection(db, 'empresas', empresaIdParam, nomeColecao);
    const nestedSnap = await getDocs(nestedRef);
    if (!nestedSnap.empty) {
      return { path: 'nested', data: nestedSnap.docs.map(d => ({ id: d.id, ...d.data() })) };
    }
    const topQ = query(collection(db, nomeColecao), where('empresaId', '==', empresaIdParam));
    const topSnap = await getDocs(topQ);
    return { path: 'top', data: topSnap.docs.map(d => ({ id: d.id, ...d.data() })) };
  }

  async function fetchDados(empresaIdParam) {
    // clientes (prefere nested > top-level)
    const nested = collection(db, 'empresas', empresaIdParam, 'clientes');
    const nestedSnap = await getDocs(nested);
    if (!nestedSnap.empty) {
      setClientes(nestedSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } else {
      const topQ = query(collection(db, 'clientes'), where('empresaId', '==', empresaIdParam));
      const topSnap = await getDocs(topQ);
      setClientes(topSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }

    // produtos / serviços
    const { path: prodPath, data: prods } = await fetchPreferNestedWithPath(empresaIdParam, 'produtos');
    const { data: servs } = await fetchPreferNestedWithPath(empresaIdParam, 'servicos');
    setProdutosPath(prodPath);
    setProdutos(prods);
    setServicos(servs);
  }

  async function fetchNotas(empresaIdParam) {
    const qy = query(colNotas(empresaIdParam), orderBy('criadaEm', 'desc'));
    const snap = await getDocs(qy);
    setNotas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function criarNotaComNumero(empresaIdParam) {
    const contadorRef = refContadorNotas(empresaIdParam);
    const novaRef = doc(colNotas(empresaIdParam));
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(contadorRef);
      let proximo = 1;
      if (!snap.exists()) {
        tx.set(contadorRef, { ultimo: proximo, atualizadoEm: serverTimestamp() });
      } else {
        proximo = Number(snap.data().ultimo || 0) + 1;
        tx.update(contadorRef, { ultimo: proximo, atualizadoEm: serverTimestamp() });
      }
      // payload será atualizado após emissão
      tx.set(novaRef, { numeroNota: proximo, criadaEm: serverTimestamp() });
    });
    return novaRef.id;
  }

  /* ====== Handlers de emissão ====== */
  // Agora enviando id/sku + campos fiscais (quando existirem) para o backend reconhecê-los
  const handleAdicionarProduto = (produtoId, quantidade = 1) => {
    const p = produtos.find(x => x.id === produtoId);
    if (!p) return;

    const entrada = {
      // identificadores que o backend reconhece
      id: p.id,
      sku: p.sku || p.codigo || p.codigoBarras || p.id,

      // possíveis campos fiscais do cadastro (se existirem já ajudam a evitar defaults)
      csosn: p.csosn ?? undefined,
      cst: p.cst ?? undefined,
      cfop: p.cfop ?? undefined,
      ncm: p.ncm ?? p.codigoNcm ?? undefined,
      unidade: p.unidade ?? p.unidadeComercial ?? 'UN',

      // os campos já usados pelo front
      produtoId: p.id,
      nome: p.nome || p.descricao || '-',
      precoVenda: Number(p.precoVenda || p.preco || 0),
      quantidade: Number(quantidade || 1),
    };

    setForm(f => ({ ...f, produtos: [...(f.produtos || []), entrada] }));
  };

  const handleAdicionarServico = (servicoId, quantidade=1) => {
    const s = servicos.find(x => x.id === servicoId);
    if (!s) return;
    const entrada = {
      servicoId: s.id,
      descricao: s.descricao || s.nome || '-',
      valor: getServicoValor(s),
      quantidade: Number(quantidade||1)
    };
    setForm(f => ({ ...f, servicos: [...(f.servicos||[]), entrada] }));
  };

  const removerProduto = (idx) =>
    setForm(f => ({ ...f, produtos: f.produtos.filter((_, i) => i !== idx) }));
  const removerServico = (idx) =>
    setForm(f => ({ ...f, servicos: f.servicos.filter((_, i) => i !== idx) }));

  const clienteSelecionado = useMemo(
    () => clientes.find(c => c.id === form.clienteId) || null,
    [clientes, form.clienteId]
  );

  const canEmitirNFe  = form.tipo === 'nfe'  && form.clienteId && (form.produtos||[]).length > 0;
  const canEmitirNFSe = form.tipo === 'nfse' && form.clienteId && (form.servicos||[]).length > 0;
  const canEmitir = canEmitirNFe || canEmitirNFSe;

  const handleEmitir = async () => {
    try {
      if (!empresaId) return;
      if (!canEmitir) {
        return Swal.fire('Atenção', 'Selecione um cliente e adicione itens antes de emitir.', 'info');
      }
      setLoadingEmit(true);

      // cria o doc da Nota (avulsa) antes de chamar a API (com número)
      const notaId = await criarNotaComNumero(empresaId);

      // base do doc
      const baseDoc = {
        empresaId,
        tipo: form.tipo,
        clienteId: form.clienteId,
        clienteNome: clienteSelecionado?.nome || '-',
        produtos: form.tipo === 'nfe' ? (form.produtos || []) : [],
        servicos: form.tipo === 'nfse' ? (form.servicos || []) : [],
        valorTotalProdutos: form.valorTotalProdutos,
        valorTotalServicos: form.valorTotalServicos,
        valorTotalGeral: form.valorTotalGeral,
        atualizadaEm: serverTimestamp(),
      };
      await updateDoc(doc(db, 'empresas', empresaId, 'notas', notaId), baseDoc);

      // chama a emissão
      if (form.tipo === 'nfe') {
        const payload = {
          notaId,
          clienteId: form.clienteId,
          produtos: form.produtos || [],
          natureza_operacao: 'Venda de mercadoria'
        };
        const resp = await emitirNFeAvulsa(empresaId, payload);
        await updateDoc(doc(db, 'empresas', empresaId, 'notas', notaId), {
          nfeRef: resp?.ref || resp?.nfeRef || '',
          nfeStatus: resp?.status || '',
          nfeChave: (resp?.chave_nfe || '').replace(/^NFe/, ''),
          nfeNumero: resp?.numero || '',
          nfeSerie: resp?.serie || '',
          nfeDanfeUrl: resp?.caminho_danfe || '',
          nfeXmlUrl: resp?.caminho_xml_nota_fiscal || '',
          nfeUltimoErro: deleteField(),
          atualizadaEm: serverTimestamp()
        });
        Swal.fire('NF-e', resp?.mensagem || 'Envio realizado! Consulte o status em alguns segundos.', 'success');
      } else {
        const payload = {
          notaId,
          clienteId: form.clienteId,
          servicos: form.servicos || [],
        };
        const resp = await emitirNFSeAvulsa(empresaId, payload);
        const viewUrl = resp?.view_url || (resp?.ref ? nfseViewUrl(resp.ref) : '');
        await updateDoc(doc(db, 'empresas', empresaId, 'notas', notaId), {
          nfseRef: resp?.ref || resp?.nfseRef || '',
          nfseStatus: resp?.status || '',
          nfseNumero: resp?.numero || '',
          nfseSerie: resp?.serie || '',
          nfseUrl: viewUrl,
          nfsePdfUrl: resp?.pdf_url || deleteField(),
          nfseHtmlUrl: resp?.html_url || deleteField(),
          nfseUltimoErro: deleteField(),
          atualizadaEm: serverTimestamp()
        });
        Swal.fire('NFS-e', resp?.mensagem || 'Envio realizado! Consulte o status em alguns segundos.', 'success');
      }

      await fetchNotas(empresaId);
      setForm(defaultForm);
    } catch (err) {
      console.error(err);
      const api = err?.response?.data;
      const msg = api?.mensagem || api?.erro?.mensagem || api?.detalhes?.mensagem || err.message || 'Falha na emissão.';
      Swal.fire('Erro', msg, 'error');
    } finally {
      setLoadingEmit(false);
    }
  };

  /* ====== Atualizar status da nota avulsa ====== */
  const atualizarStatus = async (nota) => {
    if (!nota?.id) return;
    try {
      setLoadingRefresh(p => ({ ...p, [nota.id]: true }));
      if (nota.tipo === 'nfe') {
        if (!nota.nfeRef) return Swal.fire('NF-e', 'Referência não encontrada.', 'info');
        const resp = await consultarNFePorRef(nota.nfeRef);
        const statusLower = String(resp?.status || '').toLowerCase();
        await updateDoc(doc(db, 'empresas', empresaId, 'notas', nota.id), {
          nfeStatus: resp?.status || nota.nfeStatus || '',
          nfeChave: (resp?.chave_nfe || nota.nfeChave || '').replace(/^NFe/, ''),
          nfeNumero: resp?.numero || nota.nfeNumero || '',
          nfeSerie: resp?.serie || nota.nfeSerie || '',
          nfeDanfeUrl: resp?.caminho_danfe || nota.nfeDanfeUrl || '',
          nfeXmlUrl: resp?.caminho_xml_nota_fiscal || nota.nfeXmlUrl || '',
          nfeUltimoErro: /(erro|rejei|deneg)/i.test(statusLower)
            ? (resp?.mensagem_sefaz || resp?.mensagem || '')
            : deleteField(),
          atualizadaEm: serverTimestamp()
        });
        Swal.fire('NF-e', 'Status atualizado.', 'success');
      } else {
        if (!nota.nfseRef) return Swal.fire('NFS-e', 'Referência não encontrado.', 'info');
        const resp = await consultarNFSePorRef(nota.nfseRef);
        const viewUrl = resp?.view_url || nota.nfseUrl || nfseViewUrl(nota.nfseRef);
        await updateDoc(doc(db, 'empresas', empresaId, 'notas', nota.id), {
          nfseStatus: resp?.status || '',
          nfseNumero: resp?.numero || '',
          nfseSerie: resp?.serie || '',
          nfseUrl: viewUrl,
          nfsePdfUrl: resp?.pdf_url || deleteField(),
          nfseHtmlUrl: resp?.html_url || deleteField(),
          nfseUltimoErro: (resp?.mensagem && /erro|rejei|deneg/i.test(String(resp?.status||''))) ? resp.mensagem : deleteField(),
          atualizadaEm: serverTimestamp()
        });
        Swal.fire('NFS-e', 'Status atualizado.', 'success');
      }
      await fetchNotas(empresaId);
    } catch (err) {
      console.error(err);
      const api = err?.response?.data;
      const msg = api?.mensagem || api?.erro?.mensagem || api?.detalhes?.mensagem || err.message || 'Falha ao atualizar status.';
      Swal.fire('Erro', msg, 'error');
    } finally {
      setLoadingRefresh(p => ({ ...p, [nota.id]: false }));
    }
  };

  /* ====== Cancelar (rotas AVULSAS) ====== */
  const cancelarNota = async (nota) => {
    if (!nota?.id) return;
    try {
      const { value: justificativa } = await Swal.fire({
        title: `Cancelar ${nota.tipo?.toUpperCase()}?`,
        input: 'textarea',
        inputPlaceholder: 'Descreva a justificativa (mín. 15 caracteres)',
        showCancelButton: true,
        confirmButtonText: 'Cancelar',
        cancelButtonText: 'Voltar',
      });
      if (justificativa == null) return;

      const just = String(justificativa).trim();
      if (just.length < 15) {
        return Swal.fire('Atenção', 'A justificativa deve ter pelo menos 15 caracteres.', 'info');
      }

      setLoadingCancel(p => ({ ...p, [nota.id]: true }));

      if (nota.tipo === 'nfe') {
        await cancelarNFeAvulsaApi(empresaId, {
          ref: nota.nfeRef || '',
          notaId: nota.id,
          justificativa: just
        });
        await updateDoc(doc(db, 'empresas', empresaId, 'notas', nota.id), {
          nfeStatus: 'cancelada', atualizadaEm: serverTimestamp()
        });
        Swal.fire('NF-e', 'Cancelada com sucesso.', 'success');
      } else {
        await cancelarNFSeAvulsaApi(empresaId, {
          ref: nota.nfseRef || '',
          notaId: nota.id,
          justificativa: just
        });
        await updateDoc(doc(db, 'empresas', empresaId, 'notas', nota.id), {
          nfseStatus: 'cancelada', atualizadaEm: serverTimestamp()
        });
        Swal.fire('NFS-e', 'Cancelada com sucesso.', 'success');
      }

      await fetchNotas(empresaId);
    } catch (err) {
      console.error(err);
      const api = err?.response?.data;
      const msg = api?.mensagem || api?.erro?.mensagem || api?.detalhes?.mensagem || err.message || 'Falha ao cancelar.';
      Swal.fire('Erro', msg, 'error');
    } finally {
      setLoadingCancel(p => ({ ...p, [nota.id]: false }));
    }
  };

  /* ====== Busca de PRODUTOS (nomeSearch, + fallback local) ====== */
  useEffect(() => {
    if (!empresaId) return;

    const termRaw = buscaProduto.trim();
    if (termRaw.length < 2) { setResultadosBuscaProdutos([]); setBuscandoProdutos(false); return; }

    if (produtosPath !== 'nested') {
      setResultadosBuscaProdutos([]);
      return;
    }

    const term = normalize(termRaw);
    let cancelled = false;
    setBuscandoProdutos(true);

    const doSearch = async () => {
      try {
        const ref = collection(db, 'empresas', empresaId, 'produtos');

        const qNome = query(
          ref,
          orderBy('nomeSearch'),
          startAt(term),
          endAt(term + '\uf8ff'),
          limit(50)
        );

        const extras = [];
        if (termRaw.length >= 3) {
          extras.push(getDocs(query(ref, where('sku', '==', termRaw))));
          extras.push(getDocs(query(ref, where('codigoBarras', '==', termRaw))));
          extras.push(getDocs(query(ref, where('codigo', '==', termRaw))));
        }

        const snaps = await Promise.all([getDocs(qNome), ...extras]);
        const map = new Map();
        snaps.forEach(snap => snap.forEach(d => map.set(d.id, { id: d.id, ...d.data() })));

        if (!cancelled) setResultadosBuscaProdutos(Array.from(map.values()));
      } catch (e) {
        console.error('Busca produtos:', e);
        if (!cancelled) setResultadosBuscaProdutos([]);
      } finally {
        if (!cancelled) setBuscandoProdutos(false);
      }
    };

    const id = setTimeout(doSearch, 250); // debounce
    return () => { clearTimeout(id); cancelled = true; };
  }, [empresaId, buscaProduto, produtosPath, normalize]);

  const produtosLocalFiltrados = useMemo(() => {
    const t = normalize(buscaProduto.trim());
    if (!t) return produtos;
    return produtos.filter(p => {
      const nome = normalize(p.nome || p.descricao || '');
      const sku = normalize(String(p.sku ?? ''));
      const codigo = normalize(String(p.codigo ?? ''));
      const barras = normalize(String(p.codigoBarras ?? p.codBarras ?? ''));
      return nome.includes(t) || sku.includes(t) || codigo.includes(t) || barras.includes(t);
    });
  }, [produtos, buscaProduto, normalize]);

  const produtosExibidos = (buscaProduto.trim().length >= 2 && produtosPath === 'nested')
    ? resultadosBuscaProdutos
    : produtosLocalFiltrados;

  /* ====== Busca local de SERVIÇOS (layout igual ao de produtos) ====== */
  const servicosFiltrados = useMemo(() => {
    const t = normalize(buscaServico.trim());
    if (!t) return servicos;
    return servicos.filter(s => normalize(s.nome || s.descricao || '').includes(t));
  }, [servicos, buscaServico, normalize]);

  /* ====== Filtragens ====== */
  const clientesFiltrados = useMemo(() => {
    const b = (buscaCliente||'').toLowerCase();
    if (!b) return clientes;
    return clientes.filter(c => String(c.nome||'').toLowerCase().includes(b));
  }, [clientes, buscaCliente]);

  const notasFiltradas = useMemo(() => {
    const b = (buscaNotas||'').toLowerCase();
    if (!b) return notas;
    return notas.filter(n =>
      String(n.clienteNome||'-').toLowerCase().includes(b) ||
      String(n.tipo||'').toLowerCase().includes(b) ||
      String(n.numeroNota||'').includes(b)
    );
  }, [notas, buscaNotas]);

  if (loading) return <div className="p-6 text-center">Carregando dados…</div>;

  return (
    <div className="flex h-full relative">
      <main className="flex-grow w-full max-w-[1700px] mx-auto p-6">
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Notas Fiscais (avulsas)</h3>

          {/* Botão Tutorial */}
          <button
            onClick={() => navigate('/tutorial#notas-avulsas')}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300"
            title="Abrir tutorial de notas fiscais"
          >
            <BookOpen className="h-4 w-4" />
            Ver tutorial
          </button>
        </div>

        {/* Emissão */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-lg mb-8 border dark:border-gray-700">
          <div className="flex flex-col gap-4">
            {/* Tipo */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold">Tipo de nota:</span>
              <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
                <button
                  onClick={() => setForm(f => ({ ...f, tipo: 'nfe', servicos: [] }))}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${form.tipo === 'nfe' ? 'bg-white dark:bg-gray-900 shadow' : 'text-gray-600 dark:text-gray-300'}`}
                >
                  NF-e (Produtos)
                </button>
                <button
                  onClick={() => setForm(f => ({ ...f, tipo: 'nfse', produtos: [] }))}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${form.tipo === 'nfse' ? 'bg-white dark:bg-gray-900 shadow' : 'text-gray-600 dark:text-gray-300'}`}
                >
                  NFS-e (Serviços)
                </button>
              </div>
            </div>

            {/* Cliente */}
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold block mb-1">Cliente</label>
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar cliente pelo nome"
                    className="border px-3 py-2 pl-9 rounded-md w-full bg-white dark:bg-gray-700 dark:border-gray-600"
                    value={buscaCliente}
                    onChange={e => setBuscaCliente(e.target.value)}
                  />
                </div>
                <select
                  className="mt-2 border px-3 py-2 rounded-md w-full bg-white dark:bg-gray-700 dark:border-gray-600"
                  value={form.clienteId}
                  onChange={e => setForm(f => ({ ...f, clienteId: e.target.value }))}
                >
                  <option value="">— selecione —</option>
                  {clientesFiltrados.map(c => (
                    <option key={c.id} value={c.id}>{c.nome || '-'}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Produtos</div>
                  <div className="text-lg font-bold">{(form.produtos||[]).length}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Serviços</div>
                  <div className="text-lg font-bold">{(form.servicos||[]).length}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Total</div>
                  <div className="text-lg font-bold text-green-700 dark:text-green-400">R$ {Number(form.valorTotalGeral||0).toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Itens */}
            {form.tipo === 'nfe' ? (
              <div className="mt-2">
                {/* --- Pesquisa de produto --- */}
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <label className="text-sm font-semibold block mb-1">Pesquisar produto</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        value={buscaProduto}
                        onChange={(e) => setBuscaProduto(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && produtosExibidos.length > 0) {
                            const qtd = Number(document.getElementById('produtoQtdNF').value || 1);
                            handleAdicionarProduto(produtosExibidos[0].id, qtd);
                            setBuscaProduto('');
                          }
                        }}
                        placeholder="Digite nome, código, SKU ou barras..."
                        className="w-full pl-9 pr-8 py-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                      />
                      {buscaProduto && (
                        <button
                          type="button"
                          onClick={() => setBuscaProduto('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          aria-label="Limpar busca"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="space-y-2 max-h-48 overflow-y-auto p-1 mt-2">
                      {buscandoProdutos && (
                        <p className="text-xs text-gray-500 px-1 py-2">Buscando...</p>
                      )}
                      {!buscandoProdutos && produtosExibidos.length === 0 && (
                        <p className="text-xs text-gray-500 px-1 py-2">
                          {buscaProduto.trim().length >= 2 ? 'Nenhum produto encontrado.' : 'Digite ao menos 2 letras para buscar.'}
                        </p>
                      )}
                      {produtosExibidos.map(p => {
                        const preco = p.precoVenda ?? p.preco ?? 0;
                        return (
                          <button
                            key={p.id}
                            onClick={() => {
                              const qtd = Number(document.getElementById('produtoQtdNF').value || 1);
                              handleAdicionarProduto(p.id, qtd);
                              setBuscaProduto('');
                            }}
                            className="w-full flex items-center gap-3 p-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                          >
                            <span className="flex-1 min-w-0 pr-2 truncate" title={p.nome || p.descricao}>
                              {p.nome || p.descricao || '-'}
                            </span>
                            <span className="shrink-0 whitespace-nowrap font-mono tabular-nums text-gray-600 dark:text-gray-400">
                              R$ {(parseFloat(preco) || 0).toFixed(2)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="w-32">
                    <label className="text-sm font-semibold block mb-1">Qtd.</label>
                    <input id="produtoQtdNF" type="number" min="1" step="1" defaultValue={1} className="border px-3 py-2 rounded-md w-full bg-white dark:bg-gray-700 dark:border-gray-600" />
                  </div>
                </div>

                {(form.produtos||[]).length > 0 ? (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left">Produto</th>
                          <th className="px-3 py-2 text-right">Unit.</th>
                          <th className="px-3 py-2 text-center">Qtd</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2 text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.produtos.map((p, idx) => {
                          const unit = Number(p.precoVenda||0);
                          const qtd = Number(p.quantidade||1);
                          return (
                            <tr key={`${p.produtoId}-${idx}`} className="border-t dark:border-gray-700">
                              <td className="px-3 py-2">{p.nome || '-'}</td>
                              <td className="px-3 py-2 text-right">R$ {unit.toFixed(2)}</td>
                              <td className="px-3 py-2 text-center">{qtd}</td>
                              <td className="px-3 py-2 text-right font-semibold">R$ {(unit*qtd).toFixed(2)}</td>
                              <td className="px-3 py-2 text-center">
                                <ActionIcon tone="danger" title="Remover" tooltip="Remover" onClick={() => removerProduto(idx)}>
                                  <FaTrashAlt />
                                </ActionIcon>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 mt-2">Nenhum produto adicionado.</div>
                )}
              </div>
            ) : (
              <div className="mt-2">
                {/* --- Pesquisa de serviço (mesmo layout dos produtos) --- */}
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <label className="text-sm font-semibold block mb-1">Pesquisar serviço</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        value={buscaServico}
                        onChange={(e) => setBuscaServico(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && servicosFiltrados.length > 0) {
                            const qtd = Number(document.getElementById('servicoQtdNF').value || 1);
                            handleAdicionarServico(servicosFiltrados[0].id, qtd);
                            setBuscaServico('');
                          }
                        }}
                        placeholder="Pesquisar serviço por nome..."
                        className="w-full pl-9 pr-8 py-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                      />
                      {buscaServico && (
                        <button
                          type="button"
                          onClick={() => setBuscaServico('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          aria-label="Limpar busca"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="space-y-2 max-h-48 overflow-y-auto p-1 mt-2">
                      {servicosFiltrados.length === 0 ? (
                        <p className="text-xs text-gray-500 px-1 py-2">
                          {buscaServico.trim() ? 'Nenhum serviço encontrado.' : 'Digite para buscar.'}
                        </p>
                      ) : servicosFiltrados.map(s => {
                        const preco = getServicoValor(s);
                        return (
                          <button
                            key={s.id}
                            onClick={() => {
                              const qtd = Number(document.getElementById('servicoQtdNF').value || 1);
                              handleAdicionarServico(s.id, qtd);
                              setBuscaServico('');
                            }}
                            className="w-full flex items-center gap-3 p-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                          >
                            <span className="flex-1 min-w-0 pr-2 truncate" title={s.nome || s.descricao}>
                              {s.descricao || s.nome || '-'}
                            </span>
                            <span className="shrink-0 whitespace-nowrap font-mono tabular-nums text-gray-600 dark:text-gray-400">
                              R$ {(parseFloat(preco) || 0).toFixed(2)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="w-32">
                    <label className="text-sm font-semibold block mb-1">Qtd.</label>
                    <input id="servicoQtdNF" type="number" min="1" step="1" defaultValue={1} className="border px-3 py-2 rounded-md w-full bg-white dark:bg-gray-700 dark:border-gray-600" />
                  </div>
                </div>

                {(form.servicos||[]).length > 0 ? (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left">Serviço</th>
                          <th className="px-3 py-2 text-right">Valor</th>
                          <th className="px-3 py-2 text-center">Qtd</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2 text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.servicos.map((s, idx) => {
                          const v = Number(s.valor||0);
                          const qtd = Number(s.quantidade||1);
                          return (
                            <tr key={`${s.servicoId}-${idx}`} className="border-t dark:border-gray-700">
                              <td className="px-3 py-2">{s.descricao || '-'}</td>
                              <td className="px-3 py-2 text-right">R$ {v.toFixed(2)}</td>
                              <td className="px-3 py-2 text-center">{qtd}</td>
                              <td className="px-3 py-2 text-right font-semibold">R$ {(v*qtd).toFixed(2)}</td>
                              <td className="px-3 py-2 text-center">
                                <ActionIcon tone="danger" title="Remover" tooltip="Remover" onClick={() => removerServico(idx)}>
                                  <FaTrashAlt />
                                </ActionIcon>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 mt-2">Nenhum serviço adicionado.</div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={handleEmitir}
                disabled={!canEmitir || loadingEmit}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-70"
                title={form.tipo === 'nfe' ? 'Emitir NF-e' : 'Emitir NFS-e'}
              >
                {loadingEmit ? <FaSyncAlt className="animate-spin" /> : <FaFileInvoiceDollar />}
                {form.tipo === 'nfe' ? 'Emitir NF-e' : 'Emitir NFS-e'}
              </button>
            </div>
          </div>
        </div>

        {/* Listagem de notas avulsas */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-lg border dark:border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100">Minhas notas avulsas</h4>
            <div className="relative w-full md:w-80">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por cliente, nº nota ou tipo"
                className="border px-3 py-2 pl-9 rounded-md w-full bg-white dark:bg-gray-700 dark:border-gray-600"
                value={buscaNotas}
                onChange={e => setBuscaNotas(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Nº','Tipo','Cliente','Emissão','Total','Status','Ações'].map(h => (
                    <th
                      key={h}
                      className={`px-3 py-3 ${h === 'Ações' ? 'text-center' : 'text-left'} text-xs uppercase tracking-wide text-gray-900 dark:text-gray-300`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {notasFiltradas.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-gray-400 py-6">Nenhuma nota encontrada.</td></tr>
                ) : notasFiltradas.map(n => {
                  const isNFe = n.tipo === 'nfe';
                  const statusView = isNFe ? nfStatusView(n.nfeStatus) : nfseStatusView(n.nfseStatus);
                  const danfeHref = n.nfeChave
                    ? `${BACKEND_URL}/api/focus/baixar-danfe/${String(n.nfeChave||'').replace(/^NFe/,'')}`
                    : (n.nfeDanfeUrl && !/focusnfe\.com\.br|^\//i.test(n.nfeDanfeUrl) ? ensureFocusAbsolute(n.nfeDanfeUrl) : '');
                  const xmlHref = n.nfeChave
                    ? `${BACKEND_URL}/api/focus/baixar-xml/${String(n.nfeChave||'').replace(/^NFe/,'')}`
                    : (n.nfeXmlUrl && !/focusnfe\.com\.br|^\//i.test(n.nfeXmlUrl) ? ensureFocusAbsolute(n.nfeXmlUrl) : '');
                  const nfseHref = n.nfseUrl
                    ? (/focusnfe\.com\.br|^\//i.test(n.nfseUrl) ? nfseViewUrl(n.nfseRef) : n.nfseUrl)
                    : (n.nfseRef ? nfseViewUrl(n.nfseRef) : '');

                  return (
                    <tr key={n.id} className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                      <td className="px-3 py-3 whitespace-nowrap font-semibold">#{n.numeroNota || '...'}</td>
                      <td className="px-3 py-3 whitespace-nowrap">{isNFe ? 'NF-e' : 'NFS-e'}</td>
                      <td className="px-3 py-3 whitespace-nowrap">{n.clienteNome || '-'}</td>
                      <td className="px-3 py-3 whitespace-nowrap">{n.criadaEm?.toDate ? n.criadaEm.toDate().toLocaleString('pt-BR') : '-'}</td>
                      <td className="px-3 py-3 whitespace-nowrap font-semibold text-green-700 dark:text-green-400">R$ {Number(n.valorTotalGeral||0).toFixed(2)}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <Chip text={statusView.text} className={statusView.badge} dotClass={statusView.dot} />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        <div className="flex gap-2 justify-center">
                          <ActionIcon tone="primary" title="Detalhar" tooltip="Detalhar" onClick={() => setModalNota(n)}>
                            <FaEye />
                          </ActionIcon>

                          {/* Links (quando existirem) */}
                          {isNFe && danfeHref && (
                            <ActionIcon as="a" tone="success" title="Ver DANFe (PDF)" tooltip="Ver DANFe (PDF)" href={danfeHref} target="_blank" rel="noopener noreferrer">
                              <FaFileDownload />
                            </ActionIcon>
                          )}
                          {isNFe && xmlHref && (
                            <ActionIcon as="a" tone="success" title="Baixar XML" tooltip="Baixar XML" href={xmlHref} target="_blank" rel="noopener noreferrer">
                              <FaExternalLinkAlt />
                            </ActionIcon>
                          )}
                          {!isNFe && nfseHref && (
                            <ActionIcon as="a" tone="primary" title="Ver NFS-e" tooltip="Ver NFS-e" href={nfseHref} target="_blank" rel="noopener noreferrer">
                              <FaExternalLinkAlt />
                            </ActionIcon>
                          )}

                          {/* Atualizar */}
                          <ActionIcon tone="primary" title="Atualizar status" tooltip="Atualizar status" onClick={() => atualizarStatus(n)} disabled={!!loadingRefresh[n.id]}>
                            {loadingRefresh[n.id] ? <FaSyncAlt className="animate-spin" /> : <FaSyncAlt />}
                          </ActionIcon>

                          {/* Cancelar */}
                          <ActionIcon
                            tone="danger"
                            title="Cancelar"
                            tooltip="Cancelar"
                            onClick={() => cancelarNota(n)}
                            disabled={!!loadingCancel[n.id]}
                          >
                            <FaTrashAlt />
                          </ActionIcon>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal simples de detalhes */}
        {!!modalNota && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl p-6 border dark:border-gray-700">
              <div className="flex items-start justify-between">
                <h4 className="text-xl font-bold">Detalhes da Nota #{modalNota.numeroNota || '...'}</h4>
                <button className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-800" onClick={() => setModalNota(null)}>Fechar</button>
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <div><span className="font-semibold">Tipo:</span> {modalNota.tipo === 'nfe' ? 'NF-e' : 'NFS-e'}</div>
                <div><span className="font-semibold">Cliente:</span> {modalNota.clienteNome || '-'}</div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Status:</span>
                  {modalNota.tipo === 'nfe'
                    ? <Chip text={nfStatusView(modalNota.nfeStatus).text} className={nfStatusView(modalNota.nfeStatus).badge} dotClass={nfStatusView(modalNota.nfeStatus).dot} />
                    : <Chip text={nfseStatusView(modalNota.nfseStatus).text} className={nfseStatusView(modalNota.nfseStatus).badge} dotClass={nfseStatusView(modalNota.nfseStatus).dot} />
                  }
                </div>

                {modalNota.tipo === 'nfe' ? (
                  <>
                    <div><span className="font-semibold">Chave:</span> {modalNota.nfeChave || '-'}</div>
                    <div><span className="font-semibold">Nº/Série:</span> {modalNota.nfeNumero || '-'} / {modalNota.nfeSerie || '-'}</div>
                    <div className="truncate"><span className="font-semibold">DANFe:</span> {modalNota.nfeChave ? `${BACKEND_URL}/api/focus/baixar-danfe/${String(modalNota.nfeChave).replace(/^NFe/,'')}` : (modalNota.nfeDanfeUrl || '-')}</div>
                    <div className="truncate"><span className="font-semibold">XML:</span> {modalNota.nfeChave ? `${BACKEND_URL}/api/focus/baixar-xml/${String(modalNota.nfeChave).replace(/^NFe/,'')}` : (modalNota.nfeXmlUrl || '-')}</div>
                    {!!modalNota.nfeUltimoErro && (
                      <div className="mt-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-2">
                        {modalNota.nfeUltimoErro}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div><span className="font-semibold">Ref:</span> {modalNota.nfseRef || '-'}</div>
                    <div><span className="font-semibold">Nº/Série:</span> {modalNota.nfseNumero || '-'} / {modalNota.nfseSerie || '-'}</div>
                    <div className="truncate">
                      <span className="font-semibold">Link:</span>{' '}
                      {modalNota.nfseRef ? nfseViewUrl(modalNota.nfseRef) : (modalNota.nfseUrl || '-')}
                    </div>
                    {!!modalNota.nfseUltimoErro && (
                      <div className="mt-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-2">
                        {modalNota.nfseUltimoErro}
                      </div>
                    )}
                  </>
                )}

                <div className="pt-2 flex flex-wrap gap-4">
                  <span>Produtos: <b>R$ {Number(modalNota.valorTotalProdutos||0).toFixed(2)}</b></span>
                  <span>Serviços: <b>R$ {Number(modalNota.valorTotalServicos||0).toFixed(2)}</b></span>
                  <span>Total: <b className="text-green-700 dark:text-green-400">R$ {Number(modalNota.valorTotalGeral||0).toFixed(2)}</b></span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
